import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, BellRing, Send, RefreshCw, CheckCircle2, Circle, UserPlus } from "lucide-react";
import { downloadBlob } from "@/components/SignOnSetup";
import { CommanderLinks } from "@/components/CommanderLinks";
import { CommanderSendDialog } from "@/components/CommanderSendDialog";

const fmtDay = (d) => d.split("-").reverse().join("/");
export const fmtIso = (s) => (s ? new Date(s).toLocaleString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Australia/Sydney" }) : "");

export function ElementCard({ el, onRemind, onCommander, readOnly = false }) {
  const pct = el.expected ? Math.round((el.signed / el.expected) * 100) : 0;
  return (
    <div className="border border-black/10 bg-white" data-testid={`signon-element-${el.session}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-black/10 p-4">
        <div>
          <h3 className="font-display text-xl font-black tracking-tighter">{el.session}</h3>
          <div className="text-xs text-zinc-500">{el.start || "?"}–{el.end || "?"}{el.location ? ` · ${el.location}` : ""}</div>
        </div>
        <div className="text-right">
          <div className={`font-mono text-2xl font-bold ${el.signed === el.expected && el.expected ? "text-emerald-600" : "text-black"}`} data-testid={`signon-count-${el.session}`}>{el.signed}/{el.expected}</div>
          <div className="mt-1 h-1.5 w-32 bg-zinc-100"><div className="h-full bg-emerald-500 transition-[width]" style={{ width: `${pct}%` }} /></div>
        </div>
      </div>
      <ul className="divide-y divide-black/5 text-sm">
        {el.people.map((p) => (
          <li key={p.volunteer_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2" data-testid={`signon-person-${p.volunteer_id}`}>
            {p.signed_on ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-[#FF3B30]" />}
            <span className="font-semibold">{p.name}</span>
            <span className={`text-xs ${p.is_commander ? "font-black uppercase tracking-widest text-[#002FA7]" : "text-zinc-500"}`}>{p.role}</span>
            {p.mobile ? <a href={`tel:${p.mobile}`} className="font-mono text-xs text-zinc-500 underline-offset-2 hover:underline">{p.mobile}</a> : null}
            <span className="ml-auto text-xs" data-testid={`signon-person-status-${p.volunteer_id}`}>
              {p.signed_on ? <span className="text-emerald-700">Signed on {p.signed_at_local}</span> : <span className="text-[#FF3B30]">Not signed on{p.reminder_sent_at ? ` · reminded ${fmtIso(p.reminder_sent_at)}` : ""}</span>}
            </span>
          </li>
        ))}
        {!el.people.length ? <li className="px-4 py-3 text-xs text-zinc-500">Nobody rostered for this element.</li> : null}
      </ul>
      {readOnly ? null : <div className="flex flex-wrap items-center gap-2 border-t border-black/10 bg-zinc-50 p-3 text-xs">
        <button onClick={onRemind} data-testid={`signon-remind-btn-${el.session}`} className="inline-flex items-center gap-1.5 border border-black/20 bg-white px-3 py-1.5 hover:bg-black hover:text-white"><BellRing className="h-3.5 w-3.5" /> SMS reminder to not signed on</button>
        <button onClick={onCommander} data-testid={`signon-commander-btn-${el.session}`} className="inline-flex items-center gap-1.5 border border-[#002FA7] bg-[#002FA7] px-3 py-1.5 font-semibold text-white hover:bg-white hover:text-[#002FA7]"><Send className="h-3.5 w-3.5" /> Send status to Stage Commanders</button>
        <span className="ml-auto text-zinc-500">
          {el.reminders_sent_at ? `Reminders sent ${fmtIso(el.reminders_sent_at)}. ` : ""}
          {el.commander_report_sent_at ? `Commander report sent ${fmtIso(el.commander_report_sent_at)}.` : ""}
        </span>
      </div>}
    </div>
  );
}

export function SignOnLive({ eventId, cfg, onCfgChange }) {
  const dates = (cfg.days || []).map((d) => d.date);
  const [day, setDay] = useState(dates.includes(cfg.today) ? cfg.today : dates[0] || "");
  const [rep, setRep] = useState(null);
  const [sendEl, setSendEl] = useState(null);

  const load = useCallback(() => {
    if (!day) return;
    api.get(`/admin/events/${eventId}/signon/report`, { params: { day } }).then(({ data }) => setRep(data)).catch(() => toast.error("Could not load sign-on report"));
  }, [eventId, day]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  async function remind(session) {
    if (!window.confirm(`Send SMS reminders to everyone not signed on for ${session}?`)) return;
    try {
      const { data } = await api.post(`/admin/events/${eventId}/signon/remind`, { day, session });
      toast.success(`${data.sent} reminder SMS sent`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  }

  const total = rep ? rep.elements.reduce((a, e) => [a[0] + e.signed, a[1] + e.expected], [0, 0]) : [0, 0];
  return (
    <div className="space-y-5" data-testid="signon-live">
      {sendEl ? (
        <CommanderSendDialog eventId={eventId} day={day} el={sendEl} allElements={rep?.elements} savedExtras={cfg.extra_status_mobiles}
          onClose={() => setSendEl(null)} onSent={(data) => { setSendEl(null); if (data?.saved_extra_mobiles && onCfgChange) onCfgChange({ ...cfg, extra_status_mobiles: data.saved_extra_mobiles }); load(); }} />
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <select value={day} onChange={(e) => setDay(e.target.value)} data-testid="signon-live-day-select" className="border border-black/20 bg-white px-3 py-2 text-sm">
          {dates.map((d) => <option key={d} value={d}>{fmtDay(d)}{d === cfg.today ? " (today)" : ""}</option>)}
        </select>
        <span className="font-mono text-sm" data-testid="signon-live-total">{total[0]}/{total[1]} signed on</span>
        <span className="text-xs text-zinc-500">Updates every 15s{rep ? ` · last ${fmtIso(rep.generated_at)}` : ""}</span>
        <button onClick={load} data-testid="signon-live-refresh-btn" className="inline-flex items-center gap-1 border border-black/20 bg-white px-2 py-1.5 text-xs hover:bg-black hover:text-white"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
        <div className="ml-auto flex gap-2">
          <button onClick={() => downloadBlob(`/admin/events/${eventId}/signon/export?day=${day}`, `${cfg.slug}-sign-on-${day}.xlsx`)} data-testid="signon-export-day-btn" className="inline-flex items-center gap-1.5 border border-black bg-black px-3 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black"><Download className="h-3.5 w-3.5" /> Export this day</button>
          <button onClick={() => downloadBlob(`/admin/events/${eventId}/signon/export`, `${cfg.slug}-sign-on-all-days.xlsx`)} data-testid="signon-export-all-btn" className="inline-flex items-center gap-1.5 border border-black/20 bg-white px-3 py-2 text-xs hover:bg-black hover:text-white"><Download className="h-3.5 w-3.5" /> Export all days</button>
        </div>
      </div>
      <CommanderLinks eventId={eventId} />
      {rep ? rep.elements.map((el) => <ElementCard key={el.session} el={el} onRemind={() => remind(el.session)} onCommander={() => setSendEl(el)} />) : <div className="text-sm text-zinc-500">Loading…</div>}
      {rep?.walk_ins?.length ? (
        <div className="border border-amber-300 bg-amber-50 p-4" data-testid="signon-walkins">
          <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-amber-900"><UserPlus className="h-4 w-4" /> Not on the roster ({rep.walk_ins.length})</div>
          <ul className="mt-2 space-y-1 text-sm">
            {rep.walk_ins.map((w) => <li key={w.id} data-testid={`signon-walkin-${w.id}`}>{w.first_name} {w.last_name} · <span className="font-mono text-xs">{w.mobile}</span> · <span className="text-xs">{w.signed_at_local}</span></li>)}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
